import SelectRadio from '@/components/shared/SelectRadio';
import Button from 'ui/mobile-ds/Button';
import { Layout } from '@/components/Common/Layout';
import useClaimCreateStore, {
  ClaimCreateFormSchema,
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import Stepper from '@/components/Common/Stepper/Stepper';
import { getPatientDetails } from '@/utils';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useAppContext } from '@/context/AppContext';
import useMobileBackPress from '@/hooks/useMobileBackPress';

const Schema = ClaimCreateFormSchema.pick({
  patientId: true,
  patientName: true,
  patientRelationship: true,
});

type TSchema = z.infer<typeof Schema>;

const SelectPatient = () => {
  const formState = useClaimCreateStore((state) => state.formState);
  const setFormState = useClaimCreateStore((state) => state.setFormState);
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const setCurrentPatientDetails = useClaimCreateStore(
    (state) => state.setCurrentPatientDetails,
  );
  const memberDetails = useClaimCreateStore((state) => state.memberDetails);
  const firstMember =
    memberDetails?.length === 1 ? memberDetails[0] : undefined;

  const {
    register,
    handleSubmit,
    setValue,
    // formState: { isValid },
    watch,
  } = useForm<TSchema>({
    resolver: zodResolver(Schema),
    defaultValues: {
      patientId: formState.patientId ?? firstMember?.memberId,
      patientName: formState.patientName ?? firstMember?.name,
      patientRelationship:
        formState.patientRelationship ?? firstMember?.relationship,
    },
  });

  const { isWebView } = useAppContext();

  useMobileBackPress({
    onBackPress: () => {
      window.NativeBridge?.onNativeClose?.();
    },
    isWebView,
  });

  const watchPatientId = watch('patientId');

  const onSubmit: SubmitHandler<TSchema> = (data) => {
    setCurrentStep(EClaimCreateFlowSteps.CLAIM_FOUND);
    const { patientId } = data;
    setFormState({ ...data });
    setCurrentPatientDetails(
      memberDetails?.find((member) => {
        return getPatientDetails(member).id === patientId;
      }) ?? null,
    );
  };

  const onError: SubmitErrorHandler<TSchema> = (errors) => {
    window.dev.e(errors);
  };

  useEffect(() => {
    window?.NativeBridge?.onLoadComplete?.();
  }, []);

  return (
    <Layout.Main
      headerProps={{
        title: 'Select the patient',
        description: 'This is who you are claiming for',
        onBack: () => {
          if (isWebView) {
            window?.NativeBridge?.onNativeClose?.();
          } else {
            setCurrentStep(EClaimCreateFlowSteps.SELECT_CLAIM_TYPE);
          }
        },
        children: (
          <div className="flex justify-between md:hidden">
            <h4 className="text-sm text-mobile-hero-100 font-medium leading-5">
              Cashless Claim
            </h4>
            <span className="text-sm leading-5 text-mobile-text-hierarchy-2">
              Step 1/4
            </span>
          </div>
        ),
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        className="flex flex-1 flex-col h-full mt-3 pb-24"
      >
        <div className="flex flex-1 flex-col h-full gap-4 md:gap-5">
          {memberDetails?.map((patient) => {
            const {
              age,
              fullName,
              icon: Icon,
              relationship,
              id,
            } = getPatientDetails(patient);
            return (
              <SelectRadio
                key={id}
                title={fullName}
                description={
                  <span>
                    {relationship} &bull; {age} year{(age ?? 0 > 1) ? 's' : ''}
                  </span>
                }
                value={id}
                icon={Icon && <Icon className="w-16 h-16" />}
                id={id}
                selected={watchPatientId === id}
                {...register('patientId', {
                  required: 'Please select a patient',
                })}
                onChange={() => {
                  setValue('patientId', String(id));
                  setValue('patientName', fullName);
                  setValue('patientRelationship', patient.relationship);
                }}
              />
            );
          })}
        </div>
        <Layout.Footer isSticky containerClassName="flex justify-between">
          <Stepper
            className="hidden md:flex"
            activeStep={1}
            activeSubStep={0}
            totalSubSteps={0}
            totalSteps={4}
            title="Patient select"
            subTitle="Step 1/4"
          />
          <Button
            type="submit"
            className="ml-auto md:max-w-[260px] w-full text-base"
          >
            Continue
          </Button>
        </Layout.Footer>
      </form>
    </Layout.Main>
  );
};

export default SelectPatient;
